/**
 * The go-kit as a page you can tick with a pen.
 *
 * Printed unticked regardless of what is ticked on screen. The paper copy is
 * for the run where the laptop is not open, and pre-ticked boxes on paper are
 * worse than useless — they say something is handled when nobody has actually
 * looked. That is the opposite of the sector roster, which prints what the
 * board knows precisely so a runner can see what the last pass found; the
 * difference is that a sector sheet reports and this one instructs.
 *
 * Two columns, because it has to be one page. A go-kit list that runs onto a
 * second sheet gets its second sheet left in the printer.
 */
import { PdfDoc } from '../pdf/pdf';
import {
  checkbox, clip, contentBox, furniture, heading, notesArea, pageSize,
  stampFooters, writeRule,
} from '../pdf/page';

export interface GoKitItem {
  category: string;
  item: string;
  detail: string | null;
}

const ITEM_PT = 8;
const DETAIL_PT = 6;
const LEAD = 11.5;

export async function buildGoKitPdf(items: GoKitItem[], ward: string): Promise<Blob> {
  const doc = new PdfDoc(`${ward} — go-kit`);
  const { w, h } = pageSize(false);
  const page = doc.addPage(w, h);
  furniture(page, 'Go-kit', ward);
  const box = contentBox(w, h);

  page.text('Gather before you start. Power fails first and takes everything with it; '
    + 'the paper is what keeps you running when the rest is gone.',
            box.x0, box.y1 - 8, { size: DETAIL_PT + 0.5, gray: 0.4 });

  const gutter = 20;
  const colW = (box.x1 - box.x0 - gutter) / 2;
  // The foot of the page is reserved before anything is laid out, so the two
  // columns balance against the same bottom edge.
  const footH = 88;
  const top = box.y1 - 22;
  const bottom = box.y0 + footH;

  const groups = new Map<string, GoKitItem[]>();
  for (const i of items) {
    const g = groups.get(i.category);
    if (g) g.push(i); else groups.set(i.category, [i]);
  }

  let col = 0;
  let y = top;
  for (const [category, group] of groups) {
    const need = LEAD + group.reduce((n, i) => n + LEAD + (i.detail ? 7 : 0), 0) + 6;
    // A category that does not fit moves to the next column whole. Splitting
    // "Comms" across a fold is how half of it gets missed.
    if (y - need < bottom && col === 0) { col = 1; y = top; }
    const x = box.x0 + col * (colW + gutter);
    y = heading(page, category, x, y, colW, 7.5);

    for (const i of group) {
      checkbox(page, x, y - 1, 7.5, false);
      page.text(clip(i.item, colW - 12, ITEM_PT), x + 11, y, { size: ITEM_PT });
      y -= LEAD;
      if (i.detail) {
        page.text(clip(i.detail, colW - 12, DETAIL_PT), x + 11, y + 3.5,
                  { size: DETAIL_PT, gray: 0.45 });
        y -= 7;
      }
    }
    y -= 5;
  }

  // Who packed it and when, then room to write down what was missing — which
  // is the only way the list gets better between activations.
  let fy = heading(page, 'Packed by', box.x0, box.y0 + footH - 12, box.x1 - box.x0);
  page.text('Name', box.x0, fy, { size: 7, gray: 0.45 });
  writeRule(page, box.x0 + 32, fy - 2, 200);
  page.text('Time', box.x0 + 260, fy, { size: 7, gray: 0.45 });
  writeRule(page, box.x0 + 288, fy - 2, 92);
  fy -= 16;
  page.text('Missing, or wished for — write it here and add it to the list afterward:',
            box.x0, fy, { size: DETAIL_PT + 0.5, gray: 0.4 });
  notesArea(page, box.x0, fy - 8, box.x1 - box.x0, fy - 8 - box.y0, 14);

  stampFooters(doc, ward, `Printed ${new Date().toLocaleDateString()}`);
  return doc.blob();
}

export const goKitFilename = (d = new Date()) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `go-kit-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}.pdf`;
};
